import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { expirationDurations } from 'services/cookie.service'

import { useAppSelector } from 'redux/hooks'
import { useActionless } from 'redux/hooks'

import { SET_DECK_STATE } from 'redux/deck/actions/types'
import { store } from 'redux/store'

export type RightPanelType = 'colors' | 'probability' | 'manaCurve' | 'qunatities' | 'condensed'
export type InteractionType = 'description' | 'comments' | 'deckHelp'

type RightPanelStatus = {
  open: boolean
  locked: boolean
  type: RightPanelType
}

type InteractionStatus = {
  open: boolean
  locked: boolean
  type: InteractionType
}

type StaticCardStatus = {
  open: boolean
}

export const useRightPanel = (): [RightPanelStatus, (updatedStatus: RightPanelStatus) => void] => {
  const [{ rpOpen, rpLock, rpType: type = 'condensed' }, setCookies] = useCookies(['rpOpen', 'rpLock', 'rpType'])

  const open = rpOpen === 'true'
  const locked = rpLock === 'true'

  const [rightPanelStatus, setRightPanelStatus] = useState<RightPanelStatus>({ open, locked, type })

  const handleSetNewStatus = (rightPanelStatus: RightPanelStatus) => {
    setCookies('rpOpen', `${rightPanelStatus.open}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('rpLock', `${rightPanelStatus.locked}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('rpType', rightPanelStatus.type, { path: '/', maxAge: expirationDurations.year })
  }

  useEffect(() => {
    // If they locked the panel and the screen to smaller than we'd allow the locked panel to be, unlock it
    // This is a bit un-elegant since it'll trigger a re-render immediately after the inital render, but better than having a locked panel on a phone
    if (locked && window.innerWidth < 600) handleSetNewStatus({ open: false, locked: false, type })
  }, [])

  useEffect(() => {
    setRightPanelStatus({ open, locked, type })
  }, [rpOpen, rpLock, type])

  return [rightPanelStatus, handleSetNewStatus]
}

const getType = (
  forceDescriptionOverride: boolean,
  forceDeckHelpOverride: boolean,
  interactionType: InteractionType | undefined,
): InteractionType => {
  const { deckHelp } = store.getState().deck

  if (forceDeckHelpOverride) return 'deckHelp'
  if (forceDescriptionOverride) return 'description'

  if (interactionType === 'deckHelp' && !deckHelp) return 'description'
  if (interactionType) return interactionType

  return 'description'
}

export const usePinnedInteractions = (): [InteractionStatus, (updatedStatus: InteractionStatus) => void] => {
  const [setDeckState] = useActionless(SET_DECK_STATE)

  const hasPrimer = useAppSelector(state => state.deck.hasPrimer)
  const loadedWithDeckHelp = useAppSelector(state => state.deck.loadedWithDeckHelp)

  const [{ interactionOpen, interactionLocked, interactionType }, setCookies] = useCookies([
    'interactionOpen',
    'interactionLocked',
    'interactionType',
  ])

  const open = interactionOpen === 'open'
  const locked = interactionLocked === 'locked' || hasPrimer || loadedWithDeckHelp
  const type = getType(hasPrimer, loadedWithDeckHelp, interactionType)

  const [interactionPanelStatus, setInteractionPanelStatus] = useState<InteractionStatus>({ open, locked, type })

  const handleSetNewStatus = (rightPanelStatus: InteractionStatus) => {
    setDeckState({ hasPrimer: false, loadedWithDeckHelp: false }) // These are only used as the default values for how to load the panel. If the user ever interacts with the panel after the initial load, just turn these off

    setCookies('interactionOpen', `${rightPanelStatus.open}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('interactionLocked', `${rightPanelStatus.locked}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('interactionType', rightPanelStatus.type, { path: '/', maxAge: expirationDurations.year })
  }

  useEffect(() => {
    setInteractionPanelStatus({ open, locked: interactionLocked === 'true' || hasPrimer || loadedWithDeckHelp, type })
  }, [interactionOpen, interactionLocked, interactionType, hasPrimer, loadedWithDeckHelp])

  return [interactionPanelStatus, handleSetNewStatus]
}
